'use client';

import { useState } from 'react';
import { ArrowRight, BarChart3, Calendar, CheckCircle, FileText, IndianRupee, Menu, MessageCircle, ShieldCheck, Star, Target, Users, Video, X, Zap } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';

export default function MainsTestSeriesComparison() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const collectorBabuPdf = 'https://collectorbabu.com/pdf/CollectorBabu_Mains_Test_Series_2026.pdf';

  const comparisonRows = [
    {
      name: 'CollectorBabu Zenith / CB One',
      tests: '13 tests',
      coverage: '8 sectionals + 5 FLTs across Essay and GS I-IV',
      feedback: '1x1 mentorship, video evaluation on every copy, group sessions, model answers',
      price: 'Rs 14,999',
      fit: 'Best for aspirants who want fewer, serious tests with personal correction loops',
      accent: 'border-blue-600 bg-blue-50',
      verdict: 'Best high-touch evaluation pick'
    },
    {
      name: 'Vision IAS GS Mains',
      tests: '30 tests',
      coverage: 'Advanced and full-length GS schedule',
      feedback: 'All India rankings, approach answers, mentoring modules',
      price: 'Varies by batch',
      fit: 'Good for large-batch benchmarking and a long structured schedule',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best national benchmarking pick'
    },
    {
      name: 'Vajiram & Ravi Mains',
      tests: '24 tests',
      coverage: '12 subject-wise tests + 12 full-length mains tests',
      feedback: '7-day evaluation claim, discussions, lectures, and mentorship',
      price: 'Varies by program',
      fit: 'Good for conventional coaching-led structure and broad GS coverage',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best legacy coaching pick'
    },
    {
      name: 'Drishti IAS Mains',
      tests: '4 to 35 tests',
      coverage: 'GS packages plus Ethics and Essay options',
      feedback: 'Model answers, flexible attempt format, video discussions',
      price: 'Package based',
      fit: 'Good for Hindi/English learners who want online/offline flexibility',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best flexible bilingual pick'
    },
    {
      name: 'ForumIAS MGP',
      tests: 'Cohort-based program',
      coverage: 'GS and Essay answer-writing guidance',
      feedback: 'Mentorship, model answers, topper copies, and self-paced discipline',
      price: 'Cohort based',
      fit: 'Good for aspirants who want a year-long answer-writing ecosystem',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best long-cycle MGP pick'
    },
    {
      name: 'PWOnlyIAS Mains Wallah',
      tests: '30 tests',
      coverage: 'Sectional, revision, simulator, and essay tests',
      feedback: 'Model answers, expert evaluation, feedback within 7 days, reviewer sessions',
      price: 'Rs 5,999 listed offer',
      fit: 'Good for budget-sensitive aspirants who still want structured practice',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best budget-volume pick'
    },
    {
      name: 'NEXT IAS MTS',
      tests: '3-month and 1-year formats',
      coverage: 'GS Mains syllabus with optional test series ecosystem',
      feedback: 'Performance analysis, model answers, online/offline mode',
      price: 'Program based',
      fit: 'Good for aspirants already using NEXT IAS classes or optional ecosystem',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best integrated coaching ecosystem pick'
    },
    {
      name: 'Insights IAS I-WIL',
      tests: 'Mains 2026 timetable based',
      coverage: 'Mains test series integrated with bridge/revision support',
      feedback: 'Writing improvement and mentorship-led test series',
      price: 'Program based',
      fit: 'Good for aspirants who follow Insights daily initiatives and revision rhythm',
      accent: 'border-gray-200 bg-white',
      verdict: 'Best daily-discipline ecosystem pick'
    }
  ];

  const collectorBabuStrengths = [
    {
      icon: Target,
      title: 'Focused 13-test calendar',
      text: 'The schedule avoids test overload: topic-wise sectionals first, then full-length Essay and GS papers near the Mains window.'
    },
    {
      icon: Video,
      title: 'Video evaluation layer',
      text: 'Answer copies are not treated as marksheets alone. The PDF states video evaluation by Anupriya Rai, AIR 640, on every copy.'
    },
    {
      icon: Users,
      title: 'Mentorship built in',
      text: 'The series includes 1x1 mentorship by Jaikrit Vatsal and group sessions by Shubhankar, AIR 52.'
    },
    {
      icon: FileText,
      title: 'Next-day model answers',
      text: 'Model answers are scheduled for the day after each test, keeping revision and correction close to the writing attempt.'
    }
  ];

  const collectorBabuHighlights = [
    { icon: Calendar, label: 'Starts', value: '3 June 2026' },
    { icon: FileText, label: 'Tests', value: '13 total' },
    { icon: IndianRupee, label: 'Listed price', value: 'Rs 14,999' },
    { icon: Video, label: 'Review', value: 'Video on every copy' }
  ];

  const scoringMatrix = [
    { factor: 'Test volume', collectorBabu: 'Medium', bigInstitutes: 'High', note: 'Bigger programs win on sheer number of papers.' },
    { factor: 'Copy-level feedback', collectorBabu: 'High', bigInstitutes: 'Medium to High', note: 'CollectorBabu is built around evaluation and video review.' },
    { factor: 'Mentorship access', collectorBabu: 'High', bigInstitutes: 'Varies', note: '1x1 mentorship is a clearer part of the CollectorBabu offer.' },
    { factor: 'Completion discipline', collectorBabu: 'High', bigInstitutes: 'Varies', note: '13 tests are easier to finish fully with analysis.' },
    { factor: 'All India benchmarking', collectorBabu: 'Limited public claim', bigInstitutes: 'Stronger', note: 'Large batches usually have broader rank comparison pools.' },
    { factor: 'Value for focused feedback', collectorBabu: 'Strong', bigInstitutes: 'Depends on package', note: 'Strong if the aspirant uses every evaluation loop seriously.' }
  ];

  const decisionCriteria = [
    'Choose a bigger national test series if your main need is percentile pressure, large All India Rank pools, and very frequent writing practice.',
    'Choose a focused evaluation program if your main bottleneck is answer structure, examiner-like feedback, and repeatable improvement after every copy.',
    'For working aspirants, a smaller but reviewed schedule can be easier to complete honestly than a very large test bundle.',
    'For first-time Mains writers, mentorship and reviewed answer copies usually matter more than the raw number of tests.'
  ];

  const faqs = [
    {
      question: 'Which is the best UPSC Mains Test Series 2026?',
      answer: 'The best UPSC Mains Test Series 2026 depends on your weakness. CollectorBabu is strong for high-touch answer evaluation, video feedback and mentorship. Vision IAS, Vajiram, Drishti, ForumIAS, PWOnlyIAS, NEXT IAS and Insights are stronger when you want larger test volume, larger peer pools or an institute-led ecosystem.'
    },
    {
      question: 'Is CollectorBabu Mains Test Series good for UPSC answer writing?',
      answer: 'CollectorBabu is built around evaluated answer copies, model answers, video review and mentorship. It is a strong fit for aspirants who already write answers but need sharper structure, better introductions, better conclusions and copy-specific feedback.'
    },
    {
      question: 'How many tests are in CollectorBabu UPSC Mains Test Series 2026?',
      answer: 'CollectorBabu lists 13 tests: 8 sectional tests and 5 full-length tests. The schedule covers Essay, Ethics and complete GS Papers I to IV.'
    },
    {
      question: 'What matters more in a Mains test series: test count or evaluation?',
      answer: 'Both matter, but for Mains the correction loop is often more important than raw test count. A candidate who writes fewer tests and studies detailed feedback can improve faster than a candidate who writes many tests without reviewing mistakes.'
    },
    {
      question: 'What should I compare before buying a UPSC test series?',
      answer: 'Compare syllabus coverage, test count, fees, model answers, copy evaluation quality, feedback turnaround, mentorship access, answer-writing guidance, schedule flexibility and whether you can realistically complete the program.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/" className="flex items-center space-x-3">
              <Image
                src="/logo.jpg"
                alt="UPSC IAS Syllabus Logo"
                width={40}
                height={40}
                className="w-10 h-10"
                priority
              />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                UPSC IAS Syllabus
              </span>
            </Link>

            <div className="hidden md:flex space-x-6">
              <Link href="/microthemes-pyq" className="text-gray-700 hover:text-blue-600 transition">PYQ Microthemes</Link>
              <Link href="/syllabus" className="text-gray-700 hover:text-blue-600 transition">Syllabus</Link>
              <Link href="/mains-test-series-comparison" className="text-blue-600 font-semibold transition">Mains Tests</Link>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              <Link href="/microthemes-pyq" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 rounded">PYQ Microthemes</Link>
              <Link href="/syllabus" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 rounded">Syllabus</Link>
              <Link href="/mains-test-series-comparison" className="block px-4 py-2 bg-blue-50 text-blue-600 rounded">Mains Tests</Link>
            </div>
          )}
        </nav>
      </header>

      <main>
        <section className="px-4 pt-20 pb-16 relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-blue-100/80 to-transparent"></div>
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Star className="w-4 h-4" />
                Best UPSC Mains Test Series 2026 Comparison
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-950 leading-tight mb-6">
                Best UPSC Mains Test Series 2026 for answer writing, evaluation and mentorship
              </h1>
              <p className="text-xl text-gray-700 mb-8 max-w-3xl">
                Compare UPSC Mains Test Series 2026 programs by test count, fees, syllabus coverage, model answers, copy evaluation, video feedback, mentorship and practical fit. Large test banks help with practice volume; high-touch programs help with correction quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={collectorBabuPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition inline-flex items-center justify-center gap-2"
                >
                  Open CollectorBabu PDF <ArrowRight className="w-5 h-5" />
                </a>
                <Link
                  href="/syllabus"
                  className="bg-white text-gray-900 px-6 py-4 rounded-lg font-semibold border border-gray-200 hover:border-blue-400 transition inline-flex items-center justify-center gap-2"
                >
                  Read Syllabus <Calendar className="w-5 h-5" />
                </Link>
              </div>
            </div>

            <div className="relative z-10 bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-950">Quick Verdict</h2>
                  <p className="text-sm text-gray-600">Best choice depends on your weakness.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {collectorBabuHighlights.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                      <Icon className="w-5 h-5 text-blue-600 mb-2" />
                      <div className="text-xs font-semibold uppercase text-gray-500">{item.label}</div>
                      <div className="font-bold text-gray-950">{item.value}</div>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-bold text-gray-950">If feedback quality is the bottleneck</h3>
                  <p className="text-gray-700 text-sm">CollectorBabu is positioned strongly with evaluation, AIR 640 video review, and 1:1 mentorship in the same ecosystem.</p>
                </div>
                <div className="border-l-4 border-gray-300 pl-4">
                  <h3 className="font-bold text-gray-950">If volume is the bottleneck</h3>
                  <p className="text-gray-700 text-sm">Vision, Vajiram, Drishti, and ForumIAS offer larger or broader test-series structures for sustained practice.</p>
                </div>
                <div className="border-l-4 border-gray-300 pl-4">
                  <h3 className="font-bold text-gray-950">If completion is the bottleneck</h3>
                  <p className="text-gray-700 text-sm">A focused 13-test plan may be easier to finish with serious post-test correction than a large package attempted casually.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 pb-16">
          <div className="max-w-7xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-950 mb-4">How we ranked UPSC Mains test series</h2>
                <p className="text-gray-700">
                  A good UPSC Mains test series should do more than release question papers. It should help you write under a 3-hour constraint, compare your answer with model answers, understand evaluator comments and improve your next paper. This page compares popular UPSC test series options from the lens of answer writing improvement.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {['UPSC-level questions', 'Mains answer evaluation', 'Model answers', 'Mentorship access', 'Schedule completion', 'Fees and value'].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="font-semibold text-gray-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-5">
              <div className="bg-gray-950 text-white rounded-2xl p-6 md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="w-7 h-7 text-yellow-300" />
                  <h2 className="text-2xl font-bold">CollectorBabu positioning</h2>
                </div>
                <p className="text-gray-300 mb-6">
                  CollectorBabu is not trying to win by offering the largest number of tests. It wins when an aspirant wants a smaller, complete, reviewed loop: write the paper, compare model answers, get copy-level evaluation, get video feedback, and course-correct with mentorship.
                </p>
                <a
                  href={collectorBabuPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-gray-950 hover:bg-blue-50 transition"
                >
                  View full schedule PDF <FileText className="w-5 h-5" />
                </a>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-xl font-bold text-gray-950 mb-4">Best-fit aspirant</h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <p>Already knows the syllabus basics but loses marks in answer structure.</p>
                  <p>Needs personal feedback more than another stack of unchecked papers.</p>
                  <p>Can commit to completing all 13 tests with serious post-test analysis.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between gap-6 mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-950 mb-3">Side-by-side comparison</h2>
                <p className="text-gray-700 max-w-3xl">
                  Public program details change often, so verify final fees and schedules before enrolling. The comparison below focuses on fit and learning design, not just test count.
                </p>
              </div>
              <BarChart3 className="hidden md:block w-12 h-12 text-blue-600" />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-200">
              <table className="w-full min-w-[1120px] text-left">
                <thead className="bg-gray-950 text-white">
                  <tr>
                    <th className="p-4 font-semibold">Program</th>
                    <th className="p-4 font-semibold">Test Volume</th>
                    <th className="p-4 font-semibold">Price / Format</th>
                    <th className="p-4 font-semibold">Coverage</th>
                    <th className="p-4 font-semibold">Feedback Model</th>
                    <th className="p-4 font-semibold">Verdict</th>
                    <th className="p-4 font-semibold">Best Fit</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.name} className={`border-t border-gray-200 ${row.name.includes('CollectorBabu') ? 'bg-blue-50' : 'bg-white'}`}>
                      <td className="p-4 align-top">
                        <div className={`border-l-4 ${row.accent} pl-3 py-1`}>
                          <span className="font-bold text-gray-950">{row.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-gray-700 align-top">{row.tests}</td>
                      <td className="p-4 text-gray-700 align-top">{row.price}</td>
                      <td className="p-4 text-gray-700 align-top">{row.coverage}</td>
                      <td className="p-4 text-gray-700 align-top">{row.feedback}</td>
                      <td className="p-4 text-gray-950 font-semibold align-top">{row.verdict}</td>
                      <td className="p-4 text-gray-700 align-top">{row.fit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-950 mb-4">Best UPSC Mains Test Series 2026: short recommendation</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-4">
                If your main goal is to practice the maximum number of papers, a large national test series from Vision IAS, Vajiram, Drishti, ForumIAS, PWOnlyIAS, NEXT IAS or Insights can make sense. These programs usually offer broader volume, bigger peer groups or a larger coaching ecosystem.
              </p>
              <p className="text-gray-700 mb-4">
                If your main goal is to improve evaluated answer copies, CollectorBabu deserves serious consideration because its Mains Test Series 2026 is positioned around a focused 13-test schedule, model answers, 1x1 mentorship and video evaluation on every copy. For many UPSC aspirants, that correction loop is what converts test writing into marks improvement.
              </p>
              <p className="text-gray-700">
                The practical rule is simple: choose volume when you lack writing discipline; choose evaluation when you write regularly but marks are stuck because of structure, depth, examples, presentation or conclusion quality.
              </p>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-950 mb-3">CollectorBabu vs big-institute test series</h2>
              <p className="text-gray-700 max-w-3xl mx-auto">
                This is the practical tradeoff: big institutes usually give more volume and bigger peer pools; CollectorBabu gives a more explicit evaluation-and-mentorship promise.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {scoringMatrix.map((item) => (
                <div key={item.factor} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="font-bold text-gray-950 mb-4">{item.factor}</h3>
                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="rounded-lg bg-blue-50 p-3">
                      <div className="text-xs text-gray-500 mb-1">CollectorBabu</div>
                      <div className="font-bold text-blue-700">{item.collectorBabu}</div>
                    </div>
                    <div className="rounded-lg bg-gray-100 p-3">
                      <div className="text-xs text-gray-500 mb-1">Others</div>
                      <div className="font-bold text-gray-800">{item.bigInstitutes}</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">{item.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-gradient-to-br from-blue-50 to-slate-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-950 mb-3">Where CollectorBabu quietly stands out</h2>
              <p className="text-gray-700 max-w-3xl mx-auto">
                The strongest case is not that it has the most tests. The stronger case is that the test, model answer, evaluation, video review, and mentorship loop live together.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {collectorBabuStrengths.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-blue-100 shadow-sm">
                    <Icon className="w-10 h-10 text-blue-600 mb-4" />
                    <h3 className="text-lg font-bold text-gray-950 mb-2">{item.title}</h3>
                    <p className="text-gray-700 text-sm">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-950 mb-6">How to choose without getting distracted by marketing</h2>
            <div className="space-y-4 mb-10">
              {decisionCriteria.map((item) => (
                <div key={item} className="flex gap-3 bg-gray-50 rounded-xl p-5">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-800">{item}</p>
                </div>
              ))}
            </div>

            <div className="bg-gray-950 text-white rounded-2xl p-8">
              <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-3">A practical recommendation</h3>
                  <p className="text-gray-300">
                    If you already write answers but do not know why marks are stuck, prioritize reviewed copies, model answer comparison, and mentor-led correction. That is the part CollectorBabu should be evaluated on.
                  </p>
                </div>
                <a
                  href={collectorBabuPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-gray-950 px-6 py-4 rounded-lg font-semibold hover:bg-blue-50 transition inline-flex items-center justify-center gap-2"
                >
                  Open PDF <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-950 mb-3">UPSC Mains Test Series 2026 FAQs</h2>
              <p className="text-gray-700">Answers to common search questions before choosing a Mains answer writing test series.</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.question} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-950 mb-2">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-950 mb-4">Sources reviewed</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
              <a className="text-blue-700 hover:underline" href={collectorBabuPdf}>CollectorBabu PDF</a>
              <a className="text-blue-700 hover:underline" href="https://www.visionias.in/old/schedule_if.php?exam=Mains&id=3229">Vision IAS GS Mains 2026</a>
              <a className="text-blue-700 hover:underline" href="https://vajiramandravi.com/all-courses/upsc-mains-test-series/">Vajiram Mains 2026</a>
              <a className="text-blue-700 hover:underline" href="https://www.drishtiias.com/upsc-mains-test-series">Drishti Mains 2026</a>
              <a className="text-blue-700 hover:underline" href="https://forumias.com/blog/wp-content/uploads/2025/01/MGP-2026-C1-11-February.pdf">ForumIAS MGP 2026</a>
              <a className="text-blue-700 hover:underline" href="https://pwonlyias.com/upsc-mains-test-series/">PWOnlyIAS Mains 2026</a>
              <a className="text-blue-700 hover:underline" href="https://www.nextias.com/upsc-mains-test-series/">NEXT IAS Mains</a>
              <a className="text-blue-700 hover:underline" href="https://instacourses.insightsonindia.com/course/i-wil-2026-mains-test-series">Insights IAS I-WIL</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-950 text-gray-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Image
                  src="/logo-bg-removed.png"
                  alt="UPSC IAS Syllabus Logo"
                  width={42}
                  height={42}
                  className="w-18 h-18"
                />
                <span className="text-white font-bold">UPSC IAS Syllabus</span>
              </div>
              <p className="text-sm">UPSC syllabus, microthemes and previous year questions.</p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link href="/microthemes-pyq" className="block hover:text-white transition">PYQ Microthemes</Link>
                <Link href="/syllabus" className="block hover:text-white transition">Syllabus</Link>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Mains</h4>
              <div className="space-y-2 text-sm">
                <Link href="/mains-test-series-comparison" className="block hover:text-white transition">Mains Test Series</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2025 UPSC IAS Syllabus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
